import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { t } from '../../hooks/useT';

type BatchPool = 'daily' | 'premium';

interface BatchEntry {
  id: string | null;
  answer: string;
  answerEn: string;
  searchQuery: string;
  aliases: string[];
  theme: string | null;
  difficulty: string;
}

interface EditableEntry extends BatchEntry {
  key: string;
  aliasesText: string;
  selected: boolean;
}

interface ImportJob {
  id: string;
  pool: BatchPool;
  status: 'running' | 'done' | 'failed';
  total: number;
  done: number;
  failed: number;
  current: string | null;
  phase: 'photo' | 'crops' | 'save' | 'pause' | null;
  waiting: { provider: string; until: number } | null;
  delayMs: number;
  startedAt: number;
  finishedAt: number | null;
  failures: { answer: string; error: string }[];
  remaining: number | null;
  error: string | null;
}

interface Props {
  pool: BatchPool;
  /** Rouvre directement la progression d'un import déjà lancé. */
  resumeJob?: boolean;
  onClose: () => void;
  onFinished: () => void;
}

const POLL_MS = 1000;

function formatMessage(template: string, values: Record<string, string | number>) {
  return Object.entries(values).reduce(
    (msg, [k, v]) => msg.replace(`{${k}}`, String(v)),
    template,
  );
}

export function AdminBatchImport({ pool, resumeJob = false, onClose, onFinished }: Props) {
  const [entries, setEntries] = useState<EditableEntry[]>([]);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(!resumeJob);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [job, setJob] = useState<ImportJob | null>(null);
  const [now, setNow] = useState(Date.now());
  const finishedNotified = useRef(false);

  useEffect(() => {
    if (resumeJob) return undefined;
    let cancelled = false;
    api<{ remaining: number; entries: BatchEntry[] }>(`/admin/puzzles/import-batch/preview?pool=${pool}`)
      .then((res) => {
        if (cancelled) return;
        setRemaining(res.remaining);
        setEntries(res.entries.map((e, i) => ({
          ...e,
          key: e.id ?? `idx-${i}`,
          aliasesText: e.aliases.join(', '),
          selected: true,
        })));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const err = e as { data?: { error?: string } };
        setError(err.data?.error === 'bank_exhausted'
          ? t('admin.puzzles.batchExhausted').replace('{bankSize}', '0').replace('{inDatabase}', '0')
          : t('common.error'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pool, resumeJob]);

  const pollJob = useCallback(async (id?: string) => {
    const path = id ? `/admin/puzzles/import-batch/job/${id}` : '/admin/puzzles/import-batch/job';
    const res = await api<{ job: ImportJob | null }>(path);
    setJob(res.job);
    return res.job;
  }, []);

  // Reprise : un import tourne peut-être déjà côté serveur.
  useEffect(() => {
    if (!resumeJob) return;
    pollJob()
      .catch(() => setError(t('common.error')))
      .finally(() => setLoading(false));
  }, [resumeJob, pollJob]);

  // Polling tant que l'import tourne.
  const running = job?.status === 'running';
  useEffect(() => {
    if (!job || !running) return undefined;
    const timer = window.setInterval(() => {
      pollJob(job.id).catch(() => { /* on réessaie au tick suivant */ });
      setNow(Date.now());
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [job?.id, running, pollJob]);

  useEffect(() => {
    if (job && job.status !== 'running' && !finishedNotified.current) {
      finishedNotified.current = true;
      onFinished();
    }
  }, [job, onFinished]);

  const update = (key: string, patch: Partial<EditableEntry>) => {
    setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, ...patch } : e)));
  };

  const selected = entries.filter((e) => e.selected && e.answer.trim());

  const handleValidate = async () => {
    if (!selected.length) {
      setError(t('admin.batch.nothing'));
      return;
    }
    setStarting(true);
    setError('');
    try {
      const res = await api<{ job: ImportJob }>('/admin/puzzles/import-batch', {
        method: 'POST',
        body: JSON.stringify({
          pool,
          entries: selected.map((e) => ({
            id: e.id,
            answer: e.answer,
            answerEn: e.answerEn,
            searchQuery: e.searchQuery,
            aliases: e.aliasesText.split(','),
            theme: e.theme,
            difficulty: e.difficulty,
          })),
        }),
      });
      setJob(res.job);
    } catch (e: unknown) {
      const err = e as { data?: { error?: string; message?: string } };
      setError(err.data?.error === 'import_running'
        ? t('admin.batch.running')
        : err.data?.message || t('common.error'));
    } finally {
      setStarting(false);
    }
  };

  const renderProgress = (j: ImportJob) => {
    const processed = j.done + j.failed;
    const percent = j.total > 0 ? Math.round((processed / j.total) * 100) : 0;
    const waitSeconds = j.waiting ? Math.max(0, Math.ceil((j.waiting.until - now) / 1000)) : 0;
    const elapsed = (j.finishedAt ?? now) - j.startedAt;
    const etaMin = processed > 0 && j.status === 'running'
      ? Math.max(1, Math.ceil(((elapsed / processed) * (j.total - processed)) / 60000))
      : null;
    const title = j.status === 'running'
      ? t('admin.batch.progressTitle')
      : j.status === 'done' ? t('admin.batch.doneTitle') : t('admin.batch.failedTitle');

    return (
      <div className="admin-batch-progress">
        <h3>{title}</h3>
        <div
          className="admin-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={j.total}
          aria-valuenow={processed}
        >
          <div className="admin-progress-bar" style={{ width: `${percent}%` }} />
        </div>
        <p className="admin-progress-label">
          {formatMessage(t('admin.batch.progress'), { done: processed, total: j.total })}
          {' · '}{percent}%
          {etaMin !== null && ` · ${formatMessage(t('admin.batch.eta'), { m: etaMin })}`}
        </p>

        {j.status === 'running' && j.current && (
          <p className="admin-progress-current">
            <span className="admin-badge">
              {j.phase ? t(`admin.batch.phase.${j.phase}`) : ''}
            </span>
            {' '}<strong>{j.current}</strong>
          </p>
        )}
        {j.status === 'running' && j.waiting && waitSeconds > 0 && (
          <p className="admin-msg admin-msg--compact">
            {formatMessage(t('admin.batch.waiting'), { provider: j.waiting.provider, s: waitSeconds })}
          </p>
        )}

        {j.status !== 'running' && (
          <p className="admin-msg admin-msg--compact">
            {formatMessage(t('admin.batch.summary'), { n: j.done, remaining: j.remaining ?? '?' })}
            {j.error && ` ${j.error}`}
          </p>
        )}
        {j.failures.length > 0 && (
          <details open={j.status !== 'running'}>
            <summary>{formatMessage(t('admin.batch.failures'), { n: j.failures.length })}</summary>
            <ul className="admin-batch-failures">
              {j.failures.map((f) => (
                <li key={`${f.answer}-${f.error}`}><strong>{f.answer}</strong> — {f.error}</li>
              ))}
            </ul>
          </details>
        )}
        {j.status === 'running' && <p className="admin-crop-hint">{t('admin.batch.keepOpen')}</p>}
      </div>
    );
  };

  return (
    <div className="admin-crop-overlay" role="dialog" aria-modal="true">
      <div className="admin-batch-modal">
        <header className="admin-crop-head">
          <h2>{formatMessage(t('admin.batch.title'), { pool })}</h2>
          <button type="button" className="admin-crop-close" onClick={onClose} aria-label={t('admin.batch.cancel')}>
            ×
          </button>
        </header>

        {loading && <p className="admin-msg">{t('admin.batch.loading')}</p>}
        {error && <p className="admin-msg">{error}</p>}

        {job && renderProgress(job)}

        {!loading && !job && entries.length > 0 && (
          <>
            <p className="admin-crop-hint">{t('admin.batch.hint')}</p>
            <div className="admin-batch-table-wrap">
              <table className="admin-batch-table">
                <thead>
                  <tr>
                    <th aria-label="#" />
                    <th>{t('admin.batch.colAnswer')}</th>
                    <th>{t('admin.batch.colAnswerEn')}</th>
                    <th>{t('admin.batch.colSearch')}</th>
                    <th>{t('admin.batch.colAliases')}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr key={e.key} className={e.selected ? '' : 'is-skipped'}>
                      <td className="admin-batch-check">
                        <input
                          type="checkbox"
                          checked={e.selected}
                          onChange={(ev) => update(e.key, { selected: ev.target.checked })}
                          aria-label={`${i + 1}. ${e.answer}`}
                        />
                        <span className="admin-batch-idx">{i + 1}</span>
                      </td>
                      <td>
                        <input
                          value={e.answer}
                          onChange={(ev) => update(e.key, { answer: ev.target.value })}
                        />
                        {e.theme && <span className="admin-badge admin-badge-muted">{e.theme}</span>}
                      </td>
                      <td>
                        <input
                          value={e.answerEn}
                          onChange={(ev) => update(e.key, { answerEn: ev.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          value={e.searchQuery}
                          onChange={(ev) => update(e.key, { searchQuery: ev.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          value={e.aliasesText}
                          onChange={(ev) => update(e.key, { aliasesText: ev.target.value })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <footer className="admin-batch-footer">
              <span>
                {formatMessage(t('admin.batch.selected'), {
                  n: selected.length,
                  total: entries.length,
                  remaining,
                })}
              </span>
              <span className="admin-batch-footer-actions">
                <button type="button" className="btn secondary admin-btn-sm" onClick={onClose}>
                  {t('admin.batch.cancel')}
                </button>
                <button
                  type="button"
                  className="btn primary admin-btn-sm"
                  disabled={starting || selected.length === 0}
                  onClick={() => void handleValidate()}
                >
                  {starting
                    ? t('common.loading')
                    : formatMessage(t('admin.batch.validate'), { n: selected.length })}
                </button>
              </span>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
